import React, { useState } from 'react';

import { IconButton, useTheme } from '@mui/material';
import { Bookmark, Copy } from 'lucide-react';
import { AlineTooltip } from 'src/components';
import { useNotificationDispatch } from 'src/hooks';
import { NotificationsActionType } from 'src/notifications';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';
import { useSnapshot } from 'valtio';

import { ChatMessageStaticRenderer } from './ChatEditor';
import { SavePromptDialog } from './SavePromptDialog';
import { $chatThreadState } from '../chat-state';
import type { ChatUserMessage } from '../chat-types';

const useStyles = makeStyles()(theme => ({
  actionButton: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.common.black,
    },
    // Muted by default; icons draw with currentColor so they follow this and darken on hover.
    color: theme.palette.text.secondary,
    padding: theme.spacing(0.75),
  },
  actionsContainer: {
    alignItems: 'center',
    alignSelf: 'flex-start',
    display: 'flex',
    gap: theme.spacing(0),
    marginTop: theme.spacing(1),
  },
  messageContent: {
    // No longer need padding for buttons
  },
  userMessage: {
    '& table': {
      border: `1px solid ${theme.palette.divider}`,
      borderCollapse: 'collapse',
      marginBottom: theme.spacing(1),
      marginTop: theme.spacing(1),
      width: '100%',
    },
    '& td, & th': {
      border: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1),
      textAlign: 'left',
    },
    '& th': {
      backgroundColor: theme.palette.grey[50],
      fontWeight: 'bold',
    },
    backgroundColor: theme.palette.grey[100],
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1),
    paddingBottom: theme.spacing(0.5),
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

export function ChatPanelUserMessage({ message }: { message: ChatUserMessage }) {
  const { classes } = useStyles();
  const theme = useTheme();
  const dispatch = useNotificationDispatch();
  const state = useSnapshot($chatThreadState);
  const isSaved = state.savedMessageIds.has(message.id);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.text);
      dispatch({
        payload: {
          key: uuid4(),
          message: 'Prompt copied to clipboard.',
          variant: 'success',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const handleSavePromptClick = () => {
    if (isSaved) return;
    setDialogOpen(true);
  };

  const handlePromptSaved = () => {
    // Mark this message as saved in client state
    $chatThreadState.savedMessageIds.add(message.id);

    dispatch({
      payload: {
        key: uuid4(),
        message: 'Prompt saved successfully.',
        variant: 'success',
      },
      type: NotificationsActionType.PUSH_NOTIFICATION,
    });
  };

  return (
    <div className={classes.wrapper} id={message.id}>
      <div className={classes.userMessage}>
        <div className={classes.messageContent}>
          <ChatMessageStaticRenderer markdown={message.text} />
        </div>
        <div className={classes.actionsContainer}>
          <AlineTooltip arrow placement="top" title="Copy prompt">
            <IconButton className={classes.actionButton} size="small" onClick={handleCopy}>
              <Copy size={16} stroke="currentColor" strokeWidth={1.5} />
            </IconButton>
          </AlineTooltip>
          <AlineTooltip arrow placement="top" title={isSaved ? 'Saved' : 'Save prompt'}>
            <IconButton
              className={classes.actionButton}
              size="small"
              onClick={handleSavePromptClick}
            >
              <Bookmark
                color={isSaved ? theme.palette.text.primary : undefined}
                fill={isSaved ? theme.palette.text.primary : 'none'}
                size={16}
                strokeWidth={1.5}
              />
            </IconButton>
          </AlineTooltip>
        </div>
      </div>
      <SavePromptDialog
        open={dialogOpen}
        promptText={message.text}
        onClose={() => setDialogOpen(false)}
        onSave={handlePromptSaved}
      />
    </div>
  );
}
